import React, { useEffect, useState } from "react"
import { Avatar, Modal, notification, Tooltip, Select, Tag, Badge } from "antd"
import dayjs from "dayjs"
import duration from "dayjs/plugin/duration"
import relativeTime from "dayjs/plugin/relativeTime"
import * as XLSX from "xlsx-js-style"
import AppConsts, {
  cookieKeys,
  notificationTypes,
  emailRegex,
  moduleAvatar,
  themeByEvent,
  appStatusColors,
  AppConfiguration,
  dateFormat,
} from "./appconst"
import {
  CaretDownOutlined,
  CaretUpOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  MailFilled,
  MailOutlined,
  ManOutlined,
  PhoneFilled,
  PhoneOutlined,
  WomanOutlined,
} from "@ant-design/icons"
// import Badge from "antd/lib/badge";
import { L } from "@lib/abpUtility"
import { Status } from "@models/global"
import html2canvas from "html2canvas"
import jsPDF from "jspdf"
import ItemStatus from "@components/AppItems/ItemStatus"
import modalConfirm from "@components/Modal/ModalConfirm"
import blockUserService from "@services/blockUser/blockUserService"
dayjs.extend(duration)
dayjs.extend(relativeTime)
const { pdfType } = AppConsts
const { colorByLetter } = moduleAvatar,
  { Option } = Select

export function getBase64(img, callback) {
  const reader = new FileReader()
  reader.addEventListener("load", () => callback(reader.result))
  reader.readAsDataURL(img)
}
export function isNumber(value) {
  return typeof value === "number"
}
export function getPreviewFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })
}
export const notifyWarning = (message: string, description: string) => {
  notification.warning({ message, description, placement: "bottomLeft" })
}
export const notifyError = (title: string, content: string) => {
  Modal.error({ title, content, okText: L("BTN_CLOSE") })
}

export const notifySuccess = (message: string, description: string) => {
  notification.success({ message, description, placement: "bottomLeft" })
}

export const mapActiveStatus = (isActive) => {
  return isActive
    ? new Status(L("ACTIVE"), appStatusColors.success)
    : new Status(L("INACTIVE"), appStatusColors.error)
}

export const addItemToList = (listItem: any[], itemToList: any) => {
  if (!listItem.find((item) => item?.id === itemToList?.id)) {
    listItem.push(itemToList)
  }
}

export const mergeArraysById = (array1: any[] = [], array2: any[] = []) => {
  const result = [...array1]

  array2.forEach((item) => {
    if (!result.find((existingItem) => existingItem?.id === item?.id)) {
      result.push(item)
    }
  })

  return result
}

export const removeIdFields = (obj: any) => {
  if (Array.isArray(obj)) {
    return obj.map(removeIdFields)
  } else if (typeof obj === "object" && obj !== null) {
    const newObj: any = {}
    Object.keys(obj).forEach((key) => {
      if (key.toLowerCase() !== "id") {
        newObj[key] = removeIdFields(obj[key])
      }
    })
    return newObj
  }
  return obj
}

export const converUTCDateRangePicker = (value) => {
  const date = new Date()
  const offset = date.getTimezoneOffset() / 60

  return {
    [0]: value
      ? dayjs(value[0])
          .utcOffset(0)

          .set("hour", 0 + offset)
          .set("minute", 0)
          .set("second", 0)
          .set("millisecond", 0)
          .toISOString()
      : undefined,
    [1]: value
      ? dayjs(value[1])
          .utcOffset(0)

          .set("hour", 24 + offset - 1)
          .set("minute", 59)
          .set("second", 59)
          .set("millisecond", 999)
          .toISOString()
      : undefined,
  }
}

export const convertFilterDate = (
  currentFilter,
  newDatePicker,
  fromName?,
  toName?
) => {
  const fName = fromName ? fromName : "fromDate"
  const tName = toName ? toName : "toDate"
  const fromDate = newDatePicker
    ? dayjs(newDatePicker[0]).endOf("days").toJSON()
    : undefined
  const toDate = newDatePicker
    ? dayjs(newDatePicker[1]).endOf("days").toJSON()
    : undefined
  return { ...currentFilter, [fName]: fromDate, [tName]: toDate }
}

export const handleDownloadPdf = async (element, style, fileName) => {
  const canvas = await html2canvas(element, {
    useCORS: true,
    allowTaint: true,
    logging: true,
    scale: 0.8,
    // imageTimeout:0
  })
  const data = canvas.toDataURL("image/png")
  const pageHeight = style === pdfType.portrait ? 297 : 210
  const pdfWidth = style === pdfType.portrait ? 210 : 297
  const pdfHeight = (canvas.height * pdfWidth) / canvas.width

  const pdf = new jsPDF(style ?? "landscape", "mm", "a4") // Set A4 size

  let position = 0
  let remainingHeight = pdfHeight
  pdf.addImage(data, "WEBP", 0, position, pdfWidth, pdfHeight)
  remainingHeight -= pageHeight
  while (remainingHeight >= 0) {
    pdf.addPage() // Add new page for overflow content
    position = remainingHeight - pdfHeight
    pdf.addImage(data, "WEBP", 0, position, pdfWidth, pdfHeight)
    remainingHeight -= pageHeight
  }

  pdf.save(fileName)
}

export const tableToExcel = (docId, prefix?) => {
  const data = document.getElementById(docId)
  if (data) {
    // Remove elements with class name "scroll-bar" from the content of the data element
    const elementsToRemove = data.getElementsByClassName(
      "ant-table-cell-scrollbar"
    )
    const elementsArray = Array.from(elementsToRemove)
    elementsArray.forEach((element) => {
      element.remove()
    })
  } else {
    console.error(`Element with ID '${docId}' not found.`)
  }
  const excelFile = XLSX.utils.table_to_book(data, {
    sheet: "sheet1",
    cellStyles: true,
    dateNF: "dd/MM/yyyy",
    raw: true,
  })

  // Get the first sheet in the workbook
  const sheetName = excelFile.SheetNames[0]
  const sheet = excelFile.Sheets[sheetName]

  // Define cell style with borders
  const borderStyle = {
    border: {
      top: { color: { rgb: "000000" }, style: "thin" },
      bottom: { color: { rgb: "000000" }, style: "thin" },
      left: { color: { rgb: "000000" }, style: "thin" },
      right: { color: { rgb: "000000" }, style: "thin" },
    },
  }

  const boldStyle = { bold: true }
  // Loop through all cells and apply the border style
  for (const cell in sheet) {
    if (Object.prototype.hasOwnProperty.call(sheet, cell)) {
      // Check if the cell has a valid column index
      if (cell[0] === "!") continue // Skip non-cell properties
      const colIndex = XLSX.utils.decode_col(cell.replace(/[0-9]/g, ""))
      if (colIndex >= 0) {
        if (!sheet[cell].s) sheet[cell].s = {}
        sheet[cell].s.border = borderStyle.border
      }
      const rowIndex = XLSX.utils.decode_row(cell)
      if (rowIndex === 0) {
        sheet[cell].s = { ...sheet[cell].s, ...boldStyle }
      }
      sheet[cell].t = "s"
    }
  }
  const range = XLSX.utils.decode_range(sheet["!ref"] ?? "")

  for (let C = range.s.c; C <= range.e.c; ++C) {
    const colWidth = getColWidth(sheet, C)
    sheet["!cols"] = sheet["!cols"] || []
    sheet["!cols"][C] = { wch: colWidth }
  }

  XLSX.writeFile(excelFile, docId + prefix + ".xlsx")
}
function getColWidth(sheet, colIndex) {
  const maxWidth = 50 // Set a maximum width to avoid overly wide columns
  const cellWidths = [] as any
  const range = XLSX.utils.decode_range(sheet["!ref"])

  for (let R = range.s.r; R <= range.e.r; ++R) {
    const cellValue =
      sheet[XLSX.utils.encode_cell({ r: R, c: colIndex })]?.v || ""
    const cellTextLength = cellValue.toString().length
    cellWidths.push(cellTextLength)
  }

  // Add extra padding (you can adjust this as needed)
  const padding = 2
  const colWidth = Math.min(maxWidth, Math.max(...cellWidths) + padding)

  return colWidth
}

export function isNullOrEmpty(text) {
  if (!text) {
    return true
  }

  text = text.trim()
  return text.length < 1
}

export function isObjectUndefinedOrNull(obj) {
  return obj === undefined || obj === null
}
export function isNumeric(num) {
  return !isNaN(num)
}
export function isValidEmail(text) {
  if (!text || isNullOrEmpty(text)) {
    return false
  }
  return emailRegex.test(text)
}

export function filterOptions(input, option) {
  if (!input) return true
  const inputLower = input.toLowerCase()
  
  // Handle string children
  if (typeof option?.children === "string") {
    return option.children.toLowerCase().indexOf(inputLower) >= 0
  }
  
  // Handle React element children - try to extract text content
  if (option?.children) {
    // If children is a React element, try to get text from props
    const children = option.children
    if (typeof children === "object" && children !== null) {
      // Try to get text from React element props
      if (children.props?.children) {
        const textContent = typeof children.props.children === "string" 
          ? children.props.children 
          : String(children.props.children)
        return textContent.toLowerCase().indexOf(inputLower) >= 0
      }
      // Fallback: convert to string
      const textContent = String(children)
      return textContent.toLowerCase().indexOf(inputLower) >= 0
    }
  }
  
  // Fallback: check option props or dataOption
  if (option?.props?.dataOption) {
    const dataOption = option.props.dataOption
    const searchText = (
      dataOption.displayName ||
      dataOption.label ||
      dataOption.name ||
      dataOption.code ||
      dataOption.contactName ||
      dataOption.businessName ||
      ""
    ).toLowerCase()
    return searchText.indexOf(inputLower) >= 0
  }
  
  return false
}
export function filterOptionsWithNotSpace(input, option) {
  if (!input) return true
  const sanitizedInput = input.replace(/\s/g, "").toLowerCase()
  
  // Handle string children
  if (typeof option?.children === "string") {
    const sanitizedOption = option.children.replace(/\s/g, "").toLowerCase()
    return sanitizedOption.indexOf(sanitizedInput) >= 0
  }
  
  // Handle React element children
  if (option?.children) {
    const children = option.children
    if (typeof children === "object" && children !== null) {
      let textContent = ""
      if (children.props?.children) {
        textContent = typeof children.props.children === "string" 
          ? children.props.children 
          : String(children.props.children)
      } else {
        textContent = String(children)
      }
      const sanitizedOption = textContent.replace(/\s/g, "").toLowerCase()
      return sanitizedOption.indexOf(sanitizedInput) >= 0
    }
  }
  
  // Fallback: check option props or dataOption
  if (option?.props?.dataOption) {
    const dataOption = option.props.dataOption
    const searchText = (
      dataOption.displayName ||
      dataOption.label ||
      dataOption.name ||
      dataOption.code ||
      dataOption.contactName ||
      dataOption.businessName ||
      ""
    ).replace(/\s/g, "").toLowerCase()
    return searchText.indexOf(sanitizedInput) >= 0
  }
  
  return false
}
export function arrayToObject(arr, key, value) {
  return arr.reduce((obj, current) => {
    return { ...obj, [current[key]]: current[value] }
  }, {})
}

export function getFirstLetterAndUpperCase(text) {
  return text && text.length ? text.charAt(0).toUpperCase() : "G"
}

export function hexToRGB(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
        result[3],
        16
      )}`
    : null
}

export function getCountDownXmasMessage(loaderMessage) {
  // Find the distance between now and the count down date
  // Get today's date and time
  const countDownDate = new Date(new Date().getFullYear(), 11, 25).getTime()
  const now = new Date().getTime()
  const distance = countDownDate - now

  // Time calculations for days, hours, minutes and seconds
  const days = Math.floor(distance / (1000 * 60 * 60 * 24))
  return days === 0
    ? "Merry Christmas!"
    : (loaderMessage || "").replace("{0}", `${days}`)
}

export function initMultiLanguageField() {
  return (abp.localization.languages || []).map((lang) => {
    return { languageName: lang.name, icon: lang.icon, value: "" }
  })
}

export function getRandomInt(min, max) {
  min = Math.ceil(min)
  max = Math.floor(max)
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export function mapMultiLanguageField(existLangs) {
  return (abp.localization.languages || []).map((lang) => {
    const currentLang =
      (existLangs || []).find((item) => item.languageName === lang.name) || {}
    return { ...currentLang, languageName: lang.name, icon: lang.icon }
  })
}

export function isBetween(start, end, current) {
  // Format date to remove second
  const startStr = dayjs(start).format("MM/DD/YYYY HH:mm")
  const endStr = dayjs(end).format("MM/DD/YYYY HH:mm")
  const currentStr = dayjs(current).format("MM/DD/YYYY HH:mm")
  const mStart = dayjs(startStr)
  const mEnd = dayjs(endStr)
  const mCurrent = dayjs(currentStr)
  return mStart.isBefore(mCurrent) && mEnd.isAfter(mCurrent)
}

export function isSame(timeA, timeB) {
  const timeAStr = dayjs(timeA).format("MM/DD/YYYY HH:mm")
  const timeBStr = dayjs(timeB).format("MM/DD/YYYY HH:mm")
  const mTimeA = dayjs(timeAStr)
  const mTimeB = dayjs(timeBStr)

  return mTimeA.isSame(mTimeB)
}
export function renderCompanyAvatar(
  value,
  secondInfo?,
  row?,
  onClick?,
  thirdInfo?
) {
  if (!row) {
    row = {}
  }

  const firstLetter = getFirstLetterAndUpperCase(value || "G")
  const color = colorByLetter(firstLetter)
  return (
    <>
      <div
        className="table-cell-profile"
        onClick={() => (onClick ? onClick() : "")}
      >
        <div>
          <Avatar src={row.profilePictureUrl} style={{ background: color }}>
            {firstLetter}
          </Avatar>
        </div>
        <div className="info ml-2">
          <div className="full-name text-truncate">{value}</div>
          {secondInfo && (
            <div className="phone text-truncate">{secondInfo}</div>
          )}
          {thirdInfo && <div className="phone text-truncate">{thirdInfo}</div>}
        </div>
      </div>
    </>
  )
}

export function renderContactInfo(row) {
  return (
    <div className="small">
      {row.phone && (
        <div className="phone">
          <PhoneOutlined className="mr-1" />
          {row.phone}
        </div>
      )}
      {row.emailAddress && (
        <div className="email">
          <MailOutlined className="mr-1" />
          {row.emailAddress}
        </div>
      )}
      {row.website && (
        <div className="email">
          <GlobalOutlined className="mr-1" />
          {row.website}
        </div>
      )}
      {row.address && (
        <div className="email">
          <EnvironmentOutlined className="mr-1" />
          {row.address}
        </div>
      )}
    </div>
  )
}

export function renderAvatar(value, row?, showUserName?, secondInfo?) {
  if (!row) {
    row = {}
  }

  const firstLetter = getFirstLetterAndUpperCase(value || "G")
  const color = colorByLetter(firstLetter)
  return (
    <>
      <div className="table-cell-profile">
        <div>
          <Avatar src={row.profilePictureUrl} style={{ background: color }}>
            {firstLetter}
          </Avatar>
        </div>
        <div className="info ml-2">
          <div className="full-name text-truncate">
            {L(row.gender ? "GENDER_MR" : "GENDER_MS")} {value}
          </div>
          {secondInfo && (
            <div className="phone text-truncate text-muted">{secondInfo}</div>
          )}
          {row.phoneNumber && (
            <div className="phone text-truncate text-muted">
              {row.phoneNumber}
            </div>
          )}
          {row.emailAddress && !showUserName && (
            <div className="email text-truncate text-muted">
              {row.emailAddress}
            </div>
          )}
          {row.userName && !!showUserName && (
            <div className="phone text-truncate text-muted">{row.userName}</div>
          )}
        </div>
      </div>
    </>
  )
}

export function renderContact(phoneNumber?, email?) {
  return (
    <>
      <div className="table-cell-profile">
        <div className="info ml-2">
          {phoneNumber && (
            <div className="phone text-truncate text-muted">
              <PhoneFilled className="mr-1" />
              {phoneNumber}
            </div>
          )}
          {email && (
            <div className="email text-truncate text-muted">
              <MailFilled className="mr-1" /> {email}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export function renderGender(value) {
  return <>{value ? <ManOutlined /> : <WomanOutlined />}</>
}

export function renderOptions(options, log?, showTooltip?) {
  if (log) {
    console.log(options)
  }
  if (showTooltip === true) {
    return (options || [])?.map((option, index) => (
      <Option key={index} value={option.value || option.id}>
        <Tooltip title={option.label || option.name}>
          {option.label || option.name}
        </Tooltip>
      </Option>
    ))
  } else {
    return (options || [])?.map((option, index) => (
      <Option
        disabled={option?.disabled}
        key={index}
        value={option.value || option.id}
        dataOption={option}
      >
        {option.displayName ||
          option.label ||
          option.name ||
          option.code ||
          option.contactName ||
          option.businessName}
      </Option>
    ))
  }
}
export function renderCustomerOptions(options, log?, showTooltip?) {
  if (log) {
    console.log(options)
  }
  return (options || [])?.map((option, index) => (
    <Option key={index} value={option.id}>
      {showTooltip ? (
        <Tooltip title={`${option.phoneNumber} - ${option.emailAddress}`}>
          {option.displayName}
        </Tooltip>
      ) : (
        option.displayName
      )}
    </Option>
  ))
}
export function renderDotActive(value) {
  return (
    <ItemStatus
      color={value ? "#008A00" : "#6C757D"}
      backgroundColor={value ? "#28A745" : "#B0B0B0"}
      classname="center-content"
    />
  )
}
export function renderDotStatus(value) {
  return (
    <ItemStatus
      color={value ? "#008A00" : "#F63057"}
      backgroundColor={value ? "#28A745" : "#F63057"}
      classname="center-content"
    />
  )
}
export function renderDotActiveWithText(
  value: boolean,
  textActive?: string,
  textInActive?: string
) {
  return (
    <div className="d-flex justify-content-center ">
      <p
        style={{
          color: value ? "#096DD9" : "#EB7077",
          backgroundColor: value ? "#E6F7FF" : "#f7c5c8",
          border: `1px solid ${value ? "#91D5FF" : "#EB7077"}`,
          borderRadius: "4px",
          padding: "1px 4px",
          width: "fit-content",
          fontWeight: 600,
          textAlign: "center",
          fontSize: "11px",
        }}
      >
        {L(value ? textActive || "ACTIVE" : textInActive || "FAILURE")}
      </p>
    </div>
  )
}

export function renderDotActiveWithTextGreen(value) {
  return (
    <div className="d-flex justify-content-center ">
      <p
        style={{
          color: value ? "#1E854A" : "#961212",
          backgroundColor: value ? "#E6F9EE" : "#FCE4E4",
          borderRadius: "8px",
          padding: "6px 8px",
          width: "fit-content",
          fontWeight: 600,
          textAlign: "center",
          fontSize: "11px",
        }}
      >
        {L(value ? "TEXT_ACTIVE" : "TEXT_FAILURE")}
      </p>
    </div>
  )
}

export function renderDotActiveWithData(text, color, background) {
  return (
    <div className="d-flex justify-content-center">
      <p
        style={{
          color: color,
          backgroundColor: background,
          border: `1px solid ${color}`,
          borderRadius: "4px",
          padding: "1px 4px",
          width: "fit-content",
          fontWeight: "bold",
          textAlign: "center",
          fontSize: "11px",
        }}
      >
        {L(text)}
      </p>
    </div>
  )
}

export function renderDotComm(value: boolean) {
  return value ? <Badge color={"#FEC20C"} size="default" /> : undefined
}
export function renderPercent(value) {
  if (value) {
    value = (value * 100).toFixed(2)
  }

  return value
}
export function renderMonth(value) {
  if (value) {
    // TODO using global format
    value = dayjs(value).format("MM/YYYY")
  }

  return value
}
export function renderDate(value) {
  if (value) {
    // TODO using global format
    value = dayjs(value).format(dateFormat)
  }

  return value
}
export function renderQuarter(value) {
  if (value) {
    // TODO using global format
    value = `Q${dayjs(value).format("Q-YYYY")}`
  }

  return value
}
export function renderDateTime(value) {
  if (value) {
    // TODO using global format
    value = dayjs(value).format("DD/MM/YYYY HH:mm")
  }

  return value
}

export function renderTime(value) {
  if (value) {
    // TODO using global format
    value = dayjs(value).format("HH:mm")
  }

  return value
}

export const checkTTimeEnd = (timeEnd: string) => {
  const now = dayjs()
  const expiry = dayjs(timeEnd)
  const diff = expiry.diff(now, "seconds") // Khoảng cách tính bằng giây
  if (diff <= 0) {
    return true
  }
  return false
}

export const RenderDateRemaining = ({ expiryDate }) => {
  const calculateTimeLeft = () => {
    const now = dayjs()
    const expiry = dayjs(expiryDate)
    const diff = expiry.diff(now, "seconds") // Khoảng cách tính bằng giây
    if (diff <= 0) {
      return L("EXPIRIED")
    }
    const durationObj = dayjs.duration(diff, "seconds")
    const days = Math.floor(durationObj.asDays()) // Lấy số ngày
    const hours = durationObj.hours().toString().padStart(2, "0") // Lấy số giờ
    const minutes = durationObj.minutes().toString().padStart(2, "0") // Lấy số phút
    const seconds = durationObj.seconds().toString().padStart(2, "0") // Lấy số giây
    return `${days}D ${hours}:${minutes}:${seconds}`
  }
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft())
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)
    return () => clearInterval(timer)
  }, [expiryDate])

  return timeLeft
}

export const RenderCountDownByMinute = ({ expiryDate }) => {
  const calculateTimeLeft = () => {
    if (!expiryDate) {
      return L("UNLIMITED_TIME")
    }

    const now = dayjs()
    const expiry = dayjs(expiryDate)
    const diff = expiry.diff(now, "seconds") // Khoảng cách tính bằng giây

    if (diff <= 0) {
      return L("EXPIRIED")
    }

    const days = Math.floor(diff / (24 * 3600))
    const hours = Math.floor((diff % (24 * 3600)) / 3600)
    const minutes = Math.floor((diff % 3600) / 60)
    const seconds = diff % 60

    const parts: string[] = []
    if (days > 0) parts.push(`${days} ${L("DAY")}`)
    if (hours > 0 || days > 0)
      parts.push(`${hours.toString().padStart(2, "0")} ${L("_HOUR")}`)
    if (minutes > 0 || hours > 0 || days > 0)
      parts.push(`${minutes.toString().padStart(2, "0")} ${L("_MINUTE")}`)
    parts.push(`${seconds.toString().padStart(2, "0")} ${L("_SECOND")}`)

    return parts.join(" ")
  }

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft())

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)
    return () => clearInterval(timer)
  }, [expiryDate])

  return timeLeft
}

export const confirmPlusPoint = async (userId) => {
  return new Promise<void>((resolve) => {
    modalConfirm({
      title: <div className="flex column center-items"></div>,
      content: <>{L("DO_YOU_WANT_TO_ADD_POINTS_TO_THIS_AGENT")} </>,
      cancelText: L("NOT_ASSIGN_POINTS"),
      okText: L("ASSIGN_POINTS"),
      onOk: async () => {
        await blockUserService
          .increaseErrorCount(userId)
          .finally(() => resolve())
      },
      onClose: () => {
        resolve()
      },
    })
  })
}

export function renderIsActive(value) {
  return value === true ? (
    // <Tooltip title={L("ACTIVE")}>
    <Tag color="success">{L("ACTIVE")}</Tag>
  ) : (
    // </Tooltip>
    // <Tooltip title={L("INACTIVE")}>
    <Tag color="error">{L("INACTIVE")}</Tag>
    // </Tooltip>
  )
}
export function renderIsTrue(value) {
  return value === true ? (
    <Tooltip title={L("TRUE")}>
      <Tag color="green">{L("TRUE")}</Tag>
    </Tooltip>
  ) : (
    <Tooltip title={L("FALSE")}>
      <Tag color="red">{L("FALSE")}</Tag>
    </Tooltip>
  )
}
export function renderLogo(logoUrl, projectName, size = 64) {
  const firstLetter = getFirstLetterAndUpperCase(projectName || "G")
  const color = colorByLetter(firstLetter)
  return (
    <>
      <div className="table-cell-profile">
        <div>
          <Avatar
            shape="square"
            size={size}
            src={logoUrl}
            style={{ background: color }}
          >
            {firstLetter}
          </Avatar>
        </div>
      </div>
    </>
  )
}

export function renderTag(value, color) {
  return (
    <Tag className="cell-round mr-0" color={color}>
      {value}
    </Tag>
  )
}
export function isColorDark(hexColor: string) {
  // Hỗ trợ các dạng: #RRGGBB, #RRGGBBAA, rgb(), rgba()
  let r = 0,
    g = 0,
    b = 0,
    a = 1
  if (!hexColor) return false
  if (hexColor.startsWith("rgba")) {
    // rgba(255,255,255,0.5)
    const match = hexColor.match(/rgba?\(([^)]+)\)/)
    if (match) {
      const parts = match[1].split(",").map((x) => x.trim())
      r = parseInt(parts[0])
      g = parseInt(parts[1])
      b = parseInt(parts[2])
      if (parts[3] !== undefined) a = parseFloat(parts[3])
    }
  } else if (hexColor.startsWith("rgb")) {
    // rgb(255,255,255)
    const match = hexColor.match(/rgb\(([^)]+)\)/)
    if (match) {
      const parts = match[1].split(",").map((x) => x.trim())
      r = parseInt(parts[0])
      g = parseInt(parts[1])
      b = parseInt(parts[2])
    }
  } else {
    // Xử lý hex: #RRGGBB hoặc #RRGGBBAA
    const color = hexColor.replace("#", "")
    if (color.length === 8) {
      // #RRGGBBAA
      r = parseInt(color.substring(0, 2), 16)
      g = parseInt(color.substring(2, 4), 16)
      b = parseInt(color.substring(4, 6), 16)
      a = parseInt(color.substring(6, 8), 16) / 255
    } else if (color.length === 6) {
      r = parseInt(color.substring(0, 2), 16)
      g = parseInt(color.substring(2, 4), 16)
      b = parseInt(color.substring(4, 6), 16)
    }
  }
  // Nếu alpha < 0.5 coi như không tối (quá trong suốt)
  if (a < 0.5) return false
  // Công thức tính độ sáng
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  // Tăng ngưỡng để màu trắng xuất hiện nhiều hơn (ví dụ: 160 thay vì 128)
  return brightness < 160
}

export const StatusTag = ({
  value,
  backgroundColor = "#999",
  color = isColorDark(backgroundColor) ? "#ffffffe1" : "#000000cf",
  className = "",
  closable = false,
  ...props
}) => {
  return (
    <span
      className={`status-tag ${className}`}
      style={{
        backgroundColor: backgroundColor,
        color: color,
        ...props.style,
      }}
      onMouseDown={props.onMouseDown}
      title={props.title || value}
    >
      <span>{value}</span>
      {closable && (
        <span onClick={props.onClose} className="close-icon">
          <CloseOutlined />
        </span>
      )}
    </span>
  )
}

export const getParamValue = (searchValue) => {
  const urlParams = new URLSearchParams(searchValue)

  const paramsObject = {}
  for (const [key, value] of (urlParams?.entries() || [])) {
    paramsObject[key] = value
  }
  return paramsObject
}

export function compressImage(file, maxSize) {
  const image = new Image()
  const canvas = document.createElement("canvas")
  const dataURItoBlob = function (dataURI) {
    const bytes =
      dataURI.split(",")[0].indexOf("base64") >= 0
        ? atob(dataURI.split(",")[1])
        : unescape(dataURI.split(",")[1])
    const mime = dataURI.split(",")[0].split(":")[1].split(";")[0]
    const max = bytes.length
    const ia = new Uint8Array(max)
    for (let i = 0; i < max; i++) ia[i] = bytes.charCodeAt(i)
    return new Blob([ia], { type: mime })
  }
  const reader = new FileReader()
  const resize = function () {
    let width = image.width
    let height = image.height
    if (width > height) {
      if (width > maxSize) {
        height *= maxSize / width
        width = maxSize
      }
    } else {
      if (height > maxSize) {
        width *= maxSize / height
        height = maxSize
      }
    }
    canvas.width = width
    canvas.height = height
    canvas.getContext("2d")?.drawImage(image, 0, 0, width, height)
    const dataUrl = canvas.toDataURL("image/jpeg")
    return dataURItoBlob(dataUrl)
  }
  return new Promise(function (ok, no) {
    if (!file.type.match(/image.*/)) {
      no(new Error("Not an image"))
      return
    }
    reader.onload = function (readerEvent) {
      image.onload = function () {
        return ok(resize())
      }
      image.src = readerEvent.target?.result as string
    }
    reader.readAsDataURL(file)
  })
}

// Link prepare
export function buildFileUrlWithEncToken(fileUrl) {
  return fileUrl && fileUrl.length
    ? `${fileUrl}&encToken=${encodeURIComponent(
        abp.utils.getCookieValue(cookieKeys.encToken)
      )}`
    : ""
}

export function prepareLinkQueryString(params, url) {
  if (!isObjectUndefinedOrNull(params)) {
    let index = 0
    let query = ""
    Object.keys(params).forEach((key) => {
      const bullet = index === 0 ? "?" : "&"
      let value = params[key]
      if (Array.isArray(params[key])) {
        value = ""
        params[key].forEach((item) => {
          value += (value.length ? "&" : "") + `${key}=${item}`
        })
        query = query + bullet + value
      } else {
        query = query + bullet + key + "=" + value
      }
      index++
    })

    return url + query
  }
  return url
}

export function image2Base64(img: File | Blob | undefined) {
  if (!img) {
    return Promise.resolve("")
  }
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.addEventListener("load", () => resolve(reader.result))
    reader.readAsDataURL(img)
  })
}

export function getLocalLocale() {
  // ts trick to avoid type checking
  const _navigator: any = navigator
  return (
    _navigator.userLanguage ||
    (navigator.languages &&
      navigator.languages.length &&
      navigator.languages[0]) ||
    navigator.language ||
    _navigator.browserLanguage ||
    _navigator.systemLanguage ||
    "vi"
  )
}

export function formatCurrency(val: number, locale?: string, currency = "đ") {
  return ` ${(Math.round(val) + "").replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ","
  )} ${currency}`
}

// export function formatNumber(
//   val: string | number,
//   locale = "vi",
//   currency = "vnd"
// ) {
//   const convertedNum = Number(val);
//   if (isNaN(convertedNum)) return "";

//   let _locale = locale || getLocalLocale();

//   return new Intl.NumberFormat(_locale).format(convertedNum);
// }
export function formatNumberShort(num: number): string {
  const suffixes = ["", "K", "M", "B", "T"]
  let i = 0

  while (num >= 1000 && i < suffixes.length - 1) {
    num = num / 1000
    i++
  }

  const formatted = (Math.floor(num * 10) / 10).toString()

  return `${formatted}${suffixes[i]}`
}
export function formatNumber(val?: number, nullValue = "", prefix?: any) {
  if (!val) {
    return nullValue
  }
  return ` ${(Math.round(val ?? 0) + "").replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ","
  )} ${prefix ?? ""}`
}
export function formatDynamicPercent(val?: number) {
  if (!val) {
    return 0
  }
  const className = val >= 0 ? "text-green" : "text-red"
  const icon = val >= 0 ? <CaretUpOutlined /> : <CaretDownOutlined />

  return (
    <strong className={`${className}`}>
      {icon}
      {`${(Math.round(val * 100) / 100 + "").replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ","
      )} %`}
    </strong>
  )
}

export function formatNumberWithSignal(val: number) {
  if (val === undefined) {
    return 0
  } else if (val >= 0) {
    return `+${(Math.round(val) + "").replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
  } else {
    return `${(Math.round(val) + "").replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
  }
}

export function formatNumberFloat(
  val: number,
  locale = "vi",
  currency = "vnd"
) {
  if (!val) {
    return 0
  }
  return `${(Math.round(val * 1000) / 1000 + "").replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ","
  )}`
}
export function inputCurrencyFormatter(value, locale = "vi", symbol = "đ") {
  if (value) {
    return `${(Math.round(value) + "").replace(
      /\B(?=(\d{3})+(?!\d))/g,
      ","
    )} ${symbol}`
  } else {
    return `0 ${symbol}`
  }
}
export function inputCurrencyUSAFormatter(
  value: number,
  locale = "en",
  symbol = "$"
) {
  return `${symbol} ${(Math.round(value * 1000) / 1000 + "").replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ","
  )}`
}
export function inputCurrencyParse(value, locale = "vi", symbol = "đ") {
  return value
    .replace(symbol, "")
    .replace(" ", "")
    .replace(/\$\s?|(,*)/g, "")
}

export function inputNumberFormatter(value, locale = "vi") {
  const strValue = value + ""
  const parts = strValue.split(".")
  // Format phần số nguyên với dấu phẩy
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  // Nối lại với phần thập phân (nếu có) mà không format
  return parts.join(".")
}

export function inputNumberParse(value, locale = "vi") {
  return value.replace(/\$\s?|(,*)/g, "")
}

export function compactObject(obj) {
  const keys = Object.keys(obj)
  return keys.reduce((result, key) => {
    if (obj[key]) result[key] = obj[key]
    return result
  }, {})
}

// Notification
export function getNotificationAction(userNotification: any) {
  if (
    userNotification.notification.notificationName ===
    "App.DownloadInvalidImported"
  ) {
    return notificationTypes.download
  }
  if (
    // userNotification.notification?.data?.properties.Id &&
    // userNotification.notification?.data?.properties.Type
    userNotification.notification?.data?.properties.Type === 103 // hard code
  ) {
    return notificationTypes.gotoDetail
  }

  return notificationTypes.text
}

export function changeBackgroundByEvent(event?, type?) {
  //Start the snow default options you can also make it snow in certain elements, etc.
  const { events } = themeByEvent
  switch (event) {
    case events.xmasNight:
    case events.xmasHouse:
    case events.xmasSanta: {
      const fjs = document.getElementsByTagName("script")[0]
      if (document.getElementById("blog-xtraffic-snow-effect")) return
      const js = document.createElement("script")
      js.id = "blog-xtraffic-snow-effect"
      js.src = "assets/snow-storm.js"
      fjs.parentNode && fjs.parentNode.insertBefore(js, fjs)
      break
    }
  }
}

export function buildFileUrl(fileUrl) {
  const baseUrl = (AppConfiguration.remoteServiceBaseUrl || "").substring(
    0,
    (AppConfiguration.remoteServiceBaseUrl || "").length - 1
  )
  return fileUrl && fileUrl.length
    ? `${baseUrl}${fileUrl}?enc_auth_token=${encodeURIComponent(
        abp.utils.getCookieValue(cookieKeys.encToken)
      )}`
    : ""
}

export function inputPercentFormatter(value, locale = "vi") {
  return `${(Math.round(value * 100) / 100 + "").replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ","
  )} %`
}

export function inputPercentParse(value, locale = "vi") {
  return value
    .replace("%", "")
    .replace(" ", "")
    .replace(/\$\s?|(,*)/g, "")
}

//check and add item to list

export const getTotalCountOtherOneItem = (
  listItem: any[],
  rowKey: any,
  keyToSum: any
) => {
  let totalCount = 0
  const listOhterItem = listItem.filter((item) => item.uniqueId !== rowKey)
  listOhterItem.map((item) => {
    totalCount = totalCount + item[keyToSum]
  })
  return totalCount
}

export const lightenColor = (hex, percent) => {
  // Loại bỏ dấu #
  hex = hex?.replace("#", "")

  // Chuyển HEX sang RGB
  let r = parseInt(hex.substring(0, 2), 16)
  let g = parseInt(hex.substring(2, 4), 16)
  let b = parseInt(hex.substring(4, 6), 16)

  // Tăng độ sáng theo phần trăm
  r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)))
  g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)))
  b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)))

  // Chuyển lại sang HEX
  const newHex = `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`

  return newHex
}
const registeredSignalREvents = new Set<string>()
export function useSignalREvent(
  signalrEvent: string,
  abpEvent: string,
  handler: (data: any) => void
) {
  useEffect(() => {
    function registerEvents() {
      if (
        typeof abp?.booking?.on === "function" &&
        !registeredSignalREvents.has(signalrEvent)
      ) {
        abp.booking.on(signalrEvent, function (notification) {
          abp.event.trigger(abpEvent, notification)
        })
        registeredSignalREvents.add(signalrEvent)
      }
    }
    if (abp?.booking?.hubs?.common) {
      registerEvents()
    }

    abp.event.on(abpEvent, handler)

    return () => {
      if (typeof abp?.event?.off === "function") {
        abp.event.off(abpEvent, handler)
      }
    }
  }, [])
}

export function useSignalRBookingEvent(
  signalrEvent: string,
  abpEvent: string,
  handler: (data: any) => void
) {
  useEffect(() => {
    function registerEvents() {
      if (
        typeof abp?.booking?.on === "function" &&
        !registeredSignalREvents.has(signalrEvent)
      ) {
        abp.booking.on(signalrEvent, function (notification) {
          abp.event.trigger(abpEvent, notification)
        })
        registeredSignalREvents.add(signalrEvent)
      }
    }
    if (abp?.booking?.hubs?.common) {
      registerEvents()
    }

    abp.event.on(abpEvent, handler)

    return () => {
      if (typeof abp?.event?.off === "function") {
        abp.event.off(abpEvent, handler)
      }
    }
  }, [])
}

export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth <= breakpoint
    }
    return false
  })

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= breakpoint)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [breakpoint])

  return isMobile
}

export function convertNumberToWords(number: number): string {
  const ChuSo = [
    "không",
    "một",
    "hai",
    "ba",
    "bốn",
    "năm",
    "sáu",
    "bảy",
    "tám",
    "chín",
  ]
  const Tien = ["", "nghìn", "triệu", "tỷ", "nghìn tỷ", "triệu tỷ", "tỷ tỷ"]

  function doc3So(baso: number): string {
    const tram = Math.floor(baso / 100)
    const chuc = Math.floor((baso % 100) / 10)
    const donvi = baso % 10
    let ketQua = ""

    if (tram > 0) {
      ketQua += ChuSo[tram] + " trăm"
      if (chuc == 0 && donvi > 0) ketQua += " linh"
    }

    if (chuc > 0 && chuc != 1) {
      ketQua += " " + ChuSo[chuc] + " mươi"
      if (donvi == 1) ketQua += " mốt"
      else if (donvi == 5) ketQua += " lăm"
      else if (donvi > 0) ketQua += " " + ChuSo[donvi]
    } else if (chuc == 1) {
      ketQua += " mười"
      if (donvi == 1) ketQua += " một"
      else if (donvi == 5) ketQua += " lăm"
      else if (donvi > 0) ketQua += " " + ChuSo[donvi]
    } else if (chuc == 0 && donvi > 0) {
      ketQua += " " + ChuSo[donvi]
    }

    return ketQua.trim()
  }

  if (number == 0) return "Không đồng"

  let i = 0
  let so = number
  let chuoiKetQua = ""

  while (so > 0) {
    const phanBaSo = so % 1000

    if (phanBaSo > 0) {
      const docPhan = doc3So(phanBaSo)
      chuoiKetQua = docPhan + " " + Tien[i] + " " + chuoiKetQua
    }

    so = Math.floor(so / 1000)
    i++
  }

  chuoiKetQua = chuoiKetQua.trim()
  chuoiKetQua =
    chuoiKetQua.charAt(0).toUpperCase() +
    chuoiKetQua.slice(1) +
    " việt nam đồng"

  return chuoiKetQua.replace(/\s+/g, " ")
}

export function indexToLetters(index: number): string {
  let letters = ""
  while (index >= 0) {
    letters = String.fromCharCode((index % 26) + 65) + letters
    index = Math.floor(index / 26) - 1
  }
  return letters
}

export function indexToRoman(index: number): string {
  const romanNumerals: [number, string][] = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
  ]

  let num = index + 1 // vì index thường bắt đầu từ 0
  let result = ""

  for (const [value, numeral] of romanNumerals) {
    while (num >= value) {
      result += numeral
      num -= value
    }
  }

  return result
}

export const getTimeRemaining = (
  endDateStr: string | undefined,
  startDateSrt?: string | undefined
) => {
  if (!endDateStr) return ""

  const now = dayjs.utc()
  const endDate = dayjs.utc(endDateStr)
  const startDate = startDateSrt ? dayjs.utc(startDateSrt) : undefined

  let targetDate: dayjs.Dayjs

  if (startDate && now.isBefore(startDate)) {
    // Nếu hiện tại < startBooking → đếm đến startBooking
    targetDate = startDate
  } else {
    // Nếu >= startBooking → đếm đến endDate
    targetDate = endDate
  }

  let diff = targetDate.diff(now)
  if (diff < 0) diff = 0

  const d = dayjs.duration(diff)

  const totalHours = Math.floor(d.asHours()) // tổng giờ bao gồm ngày
  const minutes = d.minutes()
  const seconds = d.seconds()

  return `${totalHours} ${L("_HOUR")} ${minutes} ${L("_MINUTE")} ${seconds} ${L(
    "_SECOND"
  )}`
}

export function getPermissionDisplayNameTooltip(permissionName: string) {
  return (
    <>
      <span>{L("YOU_NEED_PERMISSION")}</span>{" "}
      <span>'{L(permissionName)}'</span>
    </>
  )
}
